import { useElements, useStripe } from '@stripe/react-stripe-js'
import type { Stripe, StripeElements } from '@stripe/stripe-js'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { GraphQLClient, useGraphQLClient } from 'modules/core/lib/graphql/provider'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/metrics'
import app from 'modules/core/store/models/app'

import type SubscriptionForm from 'modules/billing/lib/forms/SubscriptionForm'
import cards from 'modules/billing/store/collections/cards'

const createToken = async (
  graphQLClient: GraphQLClient | null,
  stripe: Stripe | null,
  elements: StripeElements | null,
  form: SubscriptionForm
) => {
  const name = form.get('name').value
  const previousCard = cards.current
  const card = elements?.getElement('card')

  if (!stripe || previousCard || !card) return null

  const { token } = await stripe.createToken(card, {
    name,
  })

  if (!token) {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('pricing_plans.errors.subscription_creation'),
    })
    track(graphQLClient, 'PAYMENT_METHOD_ERROR')
  }

  return token
}

interface Props {
  form: SubscriptionForm
}

const useCardValidation = ({ form }: Props) => {
  const graphQLClient = useGraphQLClient()
  const stripe = useStripe()
  const elements = useElements()

  const { running: validatingCard, performAction: validateCard } = useAsyncAction(
    async (): Promise<boolean> => {
      const token = await createToken(graphQLClient, stripe, elements, form)
      const isValid = !!token

      if (token) {
        form.setValues({
          token: token.id,
        })
      }

      track(graphQLClient, 'PAYMENT_METHOD_VALIDATION', {
        is_valid: isValid,
      })

      return Promise.resolve(isValid)
    },
    {
      successFlash: false,
    }
  )

  return {
    validateCard,
    validatingCard,
  }
}

export default useCardValidation
